import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import iconBSand from '../assets/icon-b-sand.webp'

export default function Contact() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const zohoBookingUrl = import.meta.env.VITE_ZOHO_BOOKINGS_URL?.trim()
  const zohoEmbedUrl = import.meta.env.VITE_ZOHO_BOOKINGS_EMBED_URL?.trim()
  const modalBookingUrl = zohoEmbedUrl || zohoBookingUrl

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const turnstileRef = useRef<TurnstileInstance>(null)

  useEffect(() => {
    if (!isBookingModalOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsBookingModalOpen(false)
      }
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isBookingModalOpen])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSending(true)
    setError('')

    if (!turnstileToken) {
      setError(t('kontakt.captchaError'))
      setSending(false)
      return
    }

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          company,
          message,
          lang: i18n.language,
          'cf-turnstile-response': turnstileToken,
        }),
      })
      if (res.ok) {
        navigate('/thanks')
      } else {
        setError(t('kontakt.error'))
      }
    } catch {
      setError(t('kontakt.error'))
    } finally {
      setSending(false)
      turnstileRef.current?.reset()
    }
  }

  return (
    <section id="contact" className="bg-espresso">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6 }}
        className="text-center pt-24 pb-16 px-6"
      >
        <p className="font-body text-sand/40 text-xs tracking-widest uppercase mb-3">
          {t('kontakt.kicker')}
        </p>
        <h2 className="font-headline text-sand text-4xl md:text-5xl mb-4">
          {t('kontakt.h2line1')}
          <br />
          {t('kontakt.h2line2')}
        </h2>
        <p className="font-body font-light text-sand/60 text-base leading-relaxed max-w-2xl mx-auto mb-16">
          {t('kontakt.subtext1')}
          <br className="hidden md:block" /> {t('kontakt.subtext2')}
        </p>
      </motion.div>

      {/* Two columns */}
      <div className="max-w-7xl mx-auto px-6 pb-24 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
        {/* Left – Form */}
        <form onSubmit={handleSubmit} className="space-y-0">
          <label htmlFor="name" className="sr-only">
            {t('kontakt.field1')}
          </label>
          <input
            id="name"
            type="text"
            name="name"
            required
            placeholder={t('kontakt.field1')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-transparent border-b border-sand/20 text-sand py-3 mb-6 placeholder:text-sand/30 focus:border-sand/60 focus:outline-none font-body"
          />
          <label htmlFor="email" className="sr-only">
            {t('kontakt.field2')}
          </label>
          <input
            id="email"
            type="email"
            name="email"
            required
            placeholder={t('kontakt.field2')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-transparent border-b border-sand/20 text-sand py-3 mb-6 placeholder:text-sand/30 focus:border-sand/60 focus:outline-none font-body"
          />
          <label htmlFor="company" className="sr-only">
            {t('kontakt.field3')}
          </label>
          <input
            id="company"
            type="text"
            name="company"
            placeholder={t('kontakt.field3')}
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className="w-full bg-transparent border-b border-sand/20 text-sand py-3 mb-6 placeholder:text-sand/30 focus:border-sand/60 focus:outline-none font-body"
          />
          <label htmlFor="message" className="sr-only">
            {t('kontakt.field4')}
          </label>
          <textarea
            id="message"
            name="message"
            required
            placeholder={t('kontakt.field4')}
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full bg-transparent border-b border-sand/20 text-sand py-3 mb-6 placeholder:text-sand/30 focus:border-sand/60 focus:outline-none font-body resize-none"
          />
          <Turnstile
            ref={turnstileRef}
            siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
            onSuccess={setTurnstileToken}
            onError={() => setTurnstileToken('')}
            onExpire={() => setTurnstileToken('')}
            options={{ theme: 'dark', size: 'normal' }}
            className="mb-4"
          />
          {error && <p className="text-lachs text-sm mb-4">{error}</p>}
          <button
            type="submit"
            disabled={sending}
            className="bg-rot text-sand rounded-full px-8 py-3 mt-4 font-body hover:bg-lachs transition-colors disabled:opacity-50 cursor-pointer"
          >
            {sending ? t('kontakt.sending') : t('kontakt.button')}
          </button>
        </form>

        {/* Right – Contact Info */}
        <div>
          {zohoBookingUrl && (
            <div className="mb-10 rounded-3xl border border-sand/20 bg-sand/5 p-6">
              <p className="font-body text-sand/40 text-xs tracking-widest uppercase mb-2">
                {t('kontakt.bookingKicker')}
              </p>
              <h3 className="font-headline text-sand text-3xl mb-3">{t('kontakt.bookingTitle')}</h3>
              <p className="font-body text-sand/70 text-base leading-relaxed mb-5">
                {t('kontakt.bookingText')}
              </p>
              <button
                type="button"
                onClick={() => setIsBookingModalOpen(true)}
                className="inline-flex items-center bg-rot text-sand rounded-full px-6 py-3 font-body hover:bg-lachs transition-colors"
              >
                {t('kontakt.bookingButton')}
              </button>
            </div>
          )}

          <img
            src={iconBSand}
            alt="buenas studio icon"
            loading="lazy"
            width={1138}
            height={1773}
            className="w-20 mb-8"
          />
          <p className="font-headline text-sand text-2xl mb-2">
            <a href="mailto:hi@buenas-studio.com" className="hover:text-sand/70 transition-colors">
              hi@buenas-studio.com
            </a>
          </p>
          <p className="font-body text-sand/60 text-base leading-relaxed mb-8">
            +43 664 4139250 (Eva)
            <br />
            +376 681 104 (Christina)
            <br />
            <a href="#hero" className="underline hover:text-sand/70 transition-colors">
              buenas-studio.com
            </a>
          </p>
        </div>
      </div>

      <AnimatePresence>
        {isBookingModalOpen && modalBookingUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-espresso/85 backdrop-blur-sm p-4 md:p-8"
            onClick={() => setIsBookingModalOpen(false)}
            aria-hidden="true"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ duration: 0.25 }}
              className="mx-auto h-full w-full max-w-5xl overflow-hidden rounded-3xl border border-sand/20 bg-espresso shadow-[0_20px_60px_rgba(0,0,0,0.4)]"
              role="dialog"
              aria-modal="true"
              aria-label={t('kontakt.bookingIframeTitle')}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-sand/15 px-5 py-4 md:px-6">
                <p className="font-body text-sand/80 text-sm md:text-base">
                  {t('kontakt.bookingTitle')}
                </p>
                <div className="flex items-center gap-3">
                  <a
                    href={zohoBookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-body text-sand/70 text-xs md:text-sm underline hover:text-sand transition-colors"
                  >
                    {t('kontakt.bookingOpenNewTab')}
                  </a>
                  <button
                    type="button"
                    onClick={() => setIsBookingModalOpen(false)}
                    className="font-body text-sand/80 rounded-full border border-sand/25 px-3 py-1.5 text-xs md:text-sm hover:bg-sand/10 transition-colors"
                    aria-label={t('kontakt.bookingClose')}
                  >
                    {t('kontakt.bookingClose')}
                  </button>
                </div>
              </div>

              <iframe
                title={t('kontakt.bookingIframeTitle')}
                src={modalBookingUrl}
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                className="h-[calc(100%-65px)] w-full border-0 bg-white"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
